import React, { useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, User, Database } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Temporary component to help setup first admin user
 * This can be removed once proper admin user is created
 */
export function AdminSetup() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { profile, loading, createProfile, upsertProfile, refreshProfile } = useUserProfile();
  const [creating, setCreating] = useState(false);

  const handleCreateAdminProfile = async () => {
    if (!user?.id) {
      toast.error('You must be signed in to create an admin profile');
      return;
    }

    try {
      setCreating(true);
      
      const profileData = {
        userId: user.id,
        degreePursued: 'Admin User',
        totalCUsRequired: 120,
        startTerm: '2024 Term 1',
        startDate: '2024-01-01',
        setupCompleted: true,
        role: 'admin' as const
      };

      await upsertProfile(profileData);
      await refreshProfile();
      
      toast.success('Admin profile created successfully! You can now access admin features.');
    } catch (error) {
      console.error('Error creating admin profile:', error);
      toast.error('Failed to create admin profile. Check console for details.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/30 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading profile...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Admin Setup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Authentication Status */}
          <div className="space-y-2">
            <h3 className="font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Authentication Status
            </h3>
            <div className="pl-6 space-y-1 text-sm">
              <p>Signed In: <span className={isSignedIn ? 'text-green-600' : 'text-red-600'}>{isSignedIn ? 'Yes' : 'No'}</span></p>
              <p>User ID: <span className="font-mono text-xs">{user?.id || 'Not available'}</span></p>
              <p>Email: <span>{user?.primaryEmailAddress?.emailAddress || 'Not available'}</span></p>
            </div>
          </div>

          {/* Profile Status */}
          <div className="space-y-2">
            <h3 className="font-medium flex items-center gap-2">
              <Database className="h-4 w-4" />
              Database Profile Status
            </h3>
            <div className="pl-6 space-y-1 text-sm">
              <p>Profile Exists: <span className={profile ? 'text-green-600' : 'text-red-600'}>{profile ? 'Yes' : 'No'}</span></p>
              {profile && (
                <>
                  <p>Role: <span className="font-medium">{profile.role || 'None'}</span></p>
                  <p>Setup Completed: <span className={profile.setupCompleted ? 'text-green-600' : 'text-amber-600'}>{profile.setupCompleted ? 'Yes' : 'No'}</span></p>
                </>
              )}
            </div>
          </div>

          {/* Action Section */}
          <div className="space-y-4">
            {!profile ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  You don't have a profile in the database yet. Click below to create an admin profile.
                </p>
                <Button 
                  onClick={handleCreateAdminProfile}
                  disabled={creating || !user?.id}
                  className="w-full"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating Admin Profile...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Create Admin Profile
                    </>
                  )}
                </Button>
              </div>
            ) : profile.role === 'admin' ? (
              <div className="space-y-2">
                <p className="text-sm text-green-600 font-medium">
                  ✅ You already have admin access! You can now visit /admin
                </p>
                <Button asChild className="w-full">
                  <a href="/admin">Go to Admin Dashboard</a>
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-amber-600">
                  You have a profile but no admin role. You need admin permissions to access admin features.
                </p>
                <Button 
                  onClick={handleCreateAdminProfile}
                  disabled={creating}
                  variant="outline"
                  className="w-full"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Updating to Admin...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Grant Admin Access
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 border-t pt-4">
            <p>This is a temporary setup component. Once you have admin access, you can remove this and access admin features through /admin</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 